<template>
  <v-form v-on:submit.prevent="submit()">
    <v-row row wrap>
      <v-col sm="6" >
        <v-card>
          <v-card-text>
            <h2>{{ title }}</h2>
            <div class="mt-4">
              <v-text-field
                variant="underlined"
                :label="$t('name')"
                v-model="name"
                :disabled="disabled"
              ></v-text-field>
            </div>

            <h2 v-if="role.id">{{ $t('permissionsForRole', { role: role.name }) }}</h2>
            <div
              v-for="group in groupedPermissions"
              :key="group.titleKey"
              class="permission-group"
            >
              <h3 class="text-subtitle-1 font-weight-bold mt-4 mb-1">{{ $t(group.titleKey) }}</h3>
              <v-checkbox
                :label="$t(permissionTranslationKey(permission.name))"
                v-model="permissions"
                :value="permission.id"
                :disabled="disabled || isViewPermissionLocked(group, permission)"
                hide-details
                :key="permission.id"
                v-for="permission in group.permissions"
              />
            </div>
          </v-card-text>
          <v-card-actions v-if="!disabled">
            <v-btn color="orange-darken-2" depressed type="submit" v-show="!loading">{{ buttonText }}</v-btn>
            <v-progress-circular indeterminate color="orange-darken-1" v-show="loading"/>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import { groupPermissions, permissionTranslationKey } from '@/utils/permissionGroups';

const props = defineProps({
  role: {
    type: Object,
    required: true
  },
  submit: {
    type: Function,
    required: true
  },
  onInput: {
    type: Function,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  buttonText: {
    type: String,
    default: 'Save'
  },
  title: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const store = useStore();

const name = computed({
  get: () => props.role.name,
  set: value => {
    props.onInput('name', value);
  },
});

const permissions = computed({
  get: () => props.role.permissions,
  set: value => {
    props.onInput('permissions', ensureViewPermissions(value));
  },
});

const permissionsList = computed(() => {
  return store.state.permissions.list;
});

const groupedPermissions = computed(() => groupPermissions(permissionsList.value));

const isViewPermissionLocked = (group: any, permission: any): boolean => {
  if (!group.viewPermission || group.viewPermission.id !== permission.id) return false;

  return group.permissions.some(
    (other: any) => other.id !== permission.id && permissions.value.includes(other.id)
  );
};

const ensureViewPermissions = (value: number[]): number[] => {
  const result = new Set(value);

  groupedPermissions.value.forEach(group => {
    if (!group.viewPermission) return;

    const hasOtherChecked = group.permissions.some(
      (permission: any) => permission.id !== group.viewPermission.id && result.has(permission.id)
    );

    if (hasOtherChecked) {
      result.add(group.viewPermission.id);
    }
  });

  return Array.from(result);
};
</script>
